import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
    Button,
    Container,
    Content,
    Footer as RsuiteFooter,
    Heading,
    HStack,
    Message as RsuiteMessage,
    Stack,
    toaster,
} from "rsuite";
import { TypeAttributes } from "rsuite/esm/internals/types";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";

import { flowRight, template } from "lodash";

import constants from "utils/constants";
import {
    ITenantFeatureSettingWorkFlow,
    ChangeEntityFrameworkSettingToggleValue,
    ToggleValueType,
} from "utils/tenant-workflows/tenant-workflows-types";

import {
    isOn,
    isSupported,
    toState,
} from "utils/tenant-workflows/tenant-workflows-helper";

import usePreviousState from "hooks/usePreviousState";
import useRefreshPage from "hooks/useRefreshPage";
import useRequestCancellation from "hooks/useRequestCancellation";
import useDisabledFeatureSettingNames from "hooks/useDisabledFeatureSettingNames";

import withLoader from "hocs/withLoader/withLoader";
import withError from "hocs/withError/withError";

import { getErrorMessage } from "api/defaults";
import {
    runAllCefEntities,
    runAllAxImports,
    loadDataImports,
    transferAllCefData,
    updateDataImports,
} from "api/environments/tenants/data-import-api";
import {
    loadCefWorkflows,
    updateCefToggles,
} from "api/environments/tenants/tenant-feature-settings-api";

import {
    getActiveEnvironmentId,
    getActiveEnvironmentMoniker,
} from "store/environments/environment-selectors";
import {
    getTenantCustomerType,
    getTenantId,
    getTenantMoniker,
} from "store/environments/tenants/tenants-selectors";
import { getAppSettings } from "store/app-settings/app-settings-selectors";
import { getIsUserPartner } from "store/app-global/app-global-selectors";
import { fetchEnvironments } from "store/environments/environment-thunks";
import { fetchTenants } from "store/environments/tenants/tenants-thunks";
import { setIsUnsavedChangeAvailable } from "store/common/unsaved-data/unsaved-data-actions";

import { AppDispatch, RootState } from "store/store";
import {
    DataImport as DataImportEntity,
    Message,
    MessageType,
} from "store/environments/tenants/data-import/data-import-types";
import { CustomerType } from "store/customers/add-new-customer/add-new-customer-types";

import {
    updateTenantSettingWorkFlow,
    isEverySettingWorkFlowUpdated,
    isEveryWorkflowStepCompleted,
    compareEntities,
    isToggleValueEnabled,
} from "utils/tenant-workflows/tenant-workflows-helper";
import {
    axImportNameToWorkflowTypeMapping,
    tenantFeatureSettingDefinitions,
} from "utils/tenant-workflows/tenant-workflows-definitions";
import i18n from "utils/i18n";

import SignalRTenantFeatureSetting from "components/WorkflowLog/TenantFeature/SignalRTenantFeatureSetting";
import Footer from "components/Common/Footer/Footer";
import Table from "./Table/Table";

import css from "./DataImport.module.css";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    title?: string;

    showLoader: (isVisible: boolean) => void;
    showError?: (error: string) => void;
}

const DataImport: React.FC<Props> = ({
    title = i18n.t("tenantConfig.axImportControl.header"),
    showLoader,
    showError,
}: Props) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();
    const params: Record<string, string> = useParams<{
        environmentId: string;
        tenantId: string;
    }>();

    const dispatch = useAppDispatch();

    const { environmentId, tenantId } = params;

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const activeEnvironmentMoniker = useSelector((state: RootState): string =>
        getActiveEnvironmentMoniker(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const activeTenantMoniker = useSelector((state: RootState): string =>
        getTenantMoniker(state)
    );

    const customerType = useSelector((state: RootState) =>
        getTenantCustomerType(state)
    );

    const appSettings = useSelector((state: RootState) =>
        getAppSettings(state)
    );

    const isPartner = useSelector((state: RootState) =>
        getIsUserPartner(state)
    );

    const [dataFetched, setDataFetched] = useState<boolean>(false);

    const [originEntities, setOriginEntities] = useImmer<DataImportEntity[]>(
        []
    );
    const [entities, setEntities] = useImmer<DataImportEntity[]>([]);

    const [message] = useState<Message | null>(null);

    const [previousWorkflows, workflows, setWorkflows] = usePreviousState<
        ITenantFeatureSettingWorkFlow[]
    >([]);

    const [runAllAxImportsInProgress, setRunAllAxImportsInProgress] =
        useState<boolean>(false);

    const [runAllCefsInProgress, setRunAllCefsInProgress] =
        useState<boolean>(false);

    const [disabledSettingNames, , removeDisabledSettingName] =
        useDisabledFeatureSettingNames();

    const toastOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([fetchDataImports(), fetchWorkflows()])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
            });
    };

    const fetchDataImports = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadDataImports(
                activeEnvironmentId,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                const entities = [...payload.data];

                setEntities(entities.sort(compareEntities));
                setOriginEntities(entities);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchWorkflows = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadCefWorkflows(
                activeEnvironmentMoniker,
                activeTenantMoniker,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setWorkflows(payload);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleRunAllAxImports = () => {
        setRunAllAxImportsInProgress(true);

        runAllAxImports(
            activeEnvironmentId,
            activeTenantId,
            originEntities
        ).then(([payload, statusCode]) => {
            let type: TypeAttributes.Status;
            let message: string;

            if (constants.statusCode.OK === statusCode) {
                type = "success";
                message = t(
                    "tenantConfig.axImportControl.importAllSuccessMessage"
                );
            } else {
                const error = getErrorMessage(String(payload), statusCode);

                type = "error";
                message = error;
            }

            toaster.push(
                <RsuiteMessage showIcon type={type}>
                    {message}
                </RsuiteMessage>,
                toastOptions
            );

            setRunAllAxImportsInProgress(false);
        });
    };

    const handleRunAllCefEntities = () => {
        setRunAllCefsInProgress(true);

        runAllCefEntities(
            activeEnvironmentId,
            activeTenantId,
            originEntities
        ).then(([payload, statusCode]) => {
            let type: TypeAttributes.Status;
            let message: string;

            if (constants.statusCode.OK === statusCode) {
                type = "success";
                message = t(
                    "tenantConfig.axImportControl.importAllSuccessMessage"
                );
            } else {
                const error = getErrorMessage(String(payload), statusCode);

                type = "error";
                message = error;
            }

            toaster.push(
                <RsuiteMessage showIcon type={type}>
                    {message}
                </RsuiteMessage>,
                toastOptions
            );

            setRunAllCefsInProgress(false);
        });
    };

    const getCefToggleValues = (original: DataImportEntity[]) => {
        return entities.reduce(
            (
                accumulator: ChangeEntityFrameworkSettingToggleValue[],
                currentValue
            ) => {
                if (isSupported(currentValue.cef)) {
                    const originalEntity =
                        original.find(
                            (entity) => entity.entity === currentValue.entity
                        ) || null;

                    if (
                        originalEntity === null ||
                        originalEntity.cef !== currentValue.cef
                    ) {
                        const workflowType =
                            axImportNameToWorkflowTypeMapping[
                                currentValue.entity
                            ];
                        const settingDefinition =
                            workflowType !== null
                                ? tenantFeatureSettingDefinitions[workflowType]
                                : null;
                        const workflow = workflows.find(
                            (value) => value.featureName === currentValue.entity
                        );
                        const isWorkflowCompleted =
                            !workflow ||
                            isEveryWorkflowStepCompleted(workflow.steps);
                        if (settingDefinition !== null && isWorkflowCompleted) {
                            accumulator.push({
                                settingName:
                                    settingDefinition.urlParameterValue,
                                settingToggleValue: isOn(currentValue.cef),
                                axImportToggleValue: isOn(
                                    currentValue.axImport
                                ),
                            });
                        }
                    }
                }

                return accumulator;
            },
            []
        );
    };

    const handleMarkAndTransferAll = (entity: DataImportEntity) => {
        showLoader(true);

        transferAllCefData(
            activeEnvironmentId,
            activeTenantId,
            entity.entity
        ).then(([payload, statusCode]) => {
            let type: TypeAttributes.Status;
            let message: string | Element;

            if (constants.statusCode.OK === statusCode) {
                type = "success";
                message = template(
                    t(
                        "tenantConfig.axImportControl.dataTransfer.successMessage"
                    )
                )({
                    entityName: entity.entity,
                });
            } else {
                const error = getErrorMessage(String(payload), statusCode);

                type = "error";
                message = error;
            }

            toaster.push(
                <RsuiteMessage showIcon type={type}>
                    {message}
                </RsuiteMessage>,
                toastOptions
            );

            showLoader(false);
        });
    };

    const handleEntityChanged = (entity: DataImportEntity) => {
        setEntities((draft) => {
            const item = draft.find((i) => i.entity === entity.entity);
            if (item) {
                item.axImport = entity.axImport;
                item.cef = entity.cef;
            }
        });

        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleWorkflowUpdate = (workflow: ITenantFeatureSettingWorkFlow) => {
        removeDisabledSettingName(workflow.featureName);

        setWorkflows((workflows) => {
            return updateTenantSettingWorkFlow(workflows, workflow);
        });

        if (isEveryWorkflowStepCompleted(workflow.steps)) {
            setEntities((draft) => {
                const item = draft.find(
                    (i) => i.entity === workflow.featureName
                );
                if (item) {
                    const isAxImportEnabled = isToggleValueEnabled(
                        workflow.toggleValue,
                        ToggleValueType.AxImport
                    );

                    const isCefEnabled = isToggleValueEnabled(
                        workflow.toggleValue,
                        ToggleValueType.Setting
                    );

                    item.axImport = toState(isAxImportEnabled);
                    item.cef = toState(isCefEnabled);
                }
            });

            setOriginEntities((draft) => {
                const item = draft.find(
                    (i) => i.entity === workflow.featureName
                );
                if (item) {
                    const isAxImportEnabled = isToggleValueEnabled(
                        workflow.toggleValue,
                        ToggleValueType.AxImport
                    );

                    const isCefEnabled = isToggleValueEnabled(
                        workflow.toggleValue,
                        ToggleValueType.Setting
                    );

                    item.axImport = toState(isAxImportEnabled);
                    item.cef = toState(isCefEnabled);
                }
            });
        }
    };

    const changeDataImports = async () => {
        try {
            const [payload, status] = await updateDataImports(
                activeEnvironmentId,
                activeTenantId,
                entities
            );

            if (status === constants.statusCode.OK) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const changeCefToggles = async () => {
        const cefToggleValues = getCefToggleValues(originEntities);
        if (cefToggleValues.length === 0) {
            return null;
        }

        try {
            const [payload, status] = await updateCefToggles(
                activeEnvironmentMoniker,
                activeTenantMoniker,
                cefToggleValues
            );

            if (status === constants.statusCode.OK) {
                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const handleSubmit = () => {
        showLoader?.(true);

        const promises = [changeDataImports(), changeCefToggles()];

        Promise.all(promises).then((value) => {
            showLoader?.(false);
            dispatch(setIsUnsavedChangeAvailable(false));

            let message: string;
            let type: TypeAttributes.Status;

            if (value.some((v) => typeof v == "string")) {
                const error = value
                    .filter((v) => typeof v == "string")
                    .join(". ");

                type = "error";
                message = error;
            } else {
                type = "success";
                message = t("tenantConfig.axImportControl.successSent");

                fetchData();
            }

            toaster.push(
                <RsuiteMessage showIcon type={type}>
                    {message}
                </RsuiteMessage>,
                toastOptions
            );
        });
    };

    const getMessageProps = (
        axImportMessage: Message | null
    ): {
        type: TypeAttributes.Status;
        title: string;
    } => {
        switch (axImportMessage?.type) {
            case MessageType.Information:
                return {
                    type: "info",
                    title: t("tenantConfig.emptyMessage.title.info"),
                };
            case MessageType.Warning:
            case MessageType.Error:
                return {
                    type: "warning",
                    title: t("tenantConfig.emptyMessage.title.warning"),
                };
            default:
                return {
                    type: "info",
                    title: "",
                };
        }
    };

    useEffect(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [activeEnvironmentId, activeTenantId]);

    useRefreshPage(() => {
        if (activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    });

    useEffect(() => {
        if (!activeEnvironmentId) {
            dispatch(fetchEnvironments(environmentId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (!activeTenantId && activeEnvironmentId) {
            dispatch(fetchTenants(activeEnvironmentId, tenantId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (
            entities.some((entity) => !isSupported(entity.axImport)) &&
            customerType === CustomerType.AX2012
        ) {
            setEntities((draft) =>
                draft.filter((entity) => isSupported(entity.axImport))
            );
        }
    }, [entities]);

    useEffect(() => {
        if (isEverySettingWorkFlowUpdated(previousWorkflows, workflows)) {
            toaster.push(
                <RsuiteMessage type="success" showIcon>
                    {t("tenantConfig.common.featureSettingsUpdateAlertMessage")}
                </RsuiteMessage>,
                {
                    duration: constants.alertDurationMsec,
                }
            );
        }
    }, [previousWorkflows, workflows]);

    const messageProps = getMessageProps(message);

    return (
        dataFetched && (
            <Container className={css.containerDataImport}>
                <Content className={css.content}>
                    <SignalRTenantFeatureSetting
                        tenantId={tenantId}
                        updateTenantFeatureSettingWorkFlows={(
                            workFlow: ITenantFeatureSettingWorkFlow
                        ) => {
                            handleWorkflowUpdate(workFlow);
                        }}
                    >
                        <>
                            <Heading style={{ marginBottom: "1rem" }} level={1}>
                                {title}
                            </Heading>

                            {message && (
                                <RsuiteMessage
                                    className={css.messageContainer}
                                    showIcon
                                    {...messageProps}
                                >
                                    {message.message}
                                </RsuiteMessage>
                            )}
                            {entities.length !== 0 &&
                                !isPartner &&
                                appSettings.isRunAllEnabled && (
                                    <HStack
                                        style={{ margin: "1rem 0" }}
                                        justifyContent={"flex-end"}
                                        spacing={10}
                                    >
                                        <Stack.Item alignSelf={"center"}>
                                            <Button
                                                loading={
                                                    runAllAxImportsInProgress
                                                }
                                                onClick={() => {
                                                    handleRunAllAxImports();
                                                }}
                                                appearance="primary"
                                            >
                                                {t(
                                                    "tenantConfig.axImportControl.runAllAxImports"
                                                )}
                                            </Button>
                                        </Stack.Item>

                                        {customerType === CustomerType.D365 && (
                                            <Stack.Item alignSelf={"center"}>
                                                <Button
                                                    loading={
                                                        runAllCefsInProgress
                                                    }
                                                    onClick={() => {
                                                        handleRunAllCefEntities();
                                                    }}
                                                    appearance="primary"
                                                >
                                                    {t(
                                                        "tenantConfig.axImportControl.runAllCefEntities"
                                                    )}
                                                </Button>
                                            </Stack.Item>
                                        )}
                                    </HStack>
                                )}
                        </>
                        {entities.length !== 0 && (
                            <Table
                                originEntities={originEntities}
                                entities={entities}
                                isD365Customer={
                                    customerType === CustomerType.D365
                                }
                                disabledSettingNames={disabledSettingNames}
                                workflows={workflows}
                                onEntityChanged={(entity) => {
                                    handleEntityChanged(entity);
                                }}
                                onMarkAndTransferAll={(entity) =>
                                    handleMarkAndTransferAll(entity)
                                }
                            />
                        )}
                    </SignalRTenantFeatureSetting>
                </Content>

                <RsuiteFooter>
                    <Footer onSubmit={() => handleSubmit()} />
                </RsuiteFooter>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError)(DataImport);

export default Component;
